'use client';

import React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import { StyledStatBadge, StatRingSvg, StatText, StatIcon } from './styles';

const RING_VIEWBOX = 72;
const RING_CENTER = 36;
const RING_RADIUS = 32;
const RING_STROKE_WIDTH = 8;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const StatProgressRing = ({
  stat,
  size,
  innerColor,
  trackColor,
  progressColor,
}) => {
  const theme = useTheme();
  const progress = Math.min(100, Math.max(0, stat)) / 100;

  return (
    <StatRingSvg
      width={size}
      height={size}
      viewBox={`0 0 ${RING_VIEWBOX} ${RING_VIEWBOX}`}
      aria-hidden="true"
    >
      <circle
        cx={RING_CENTER}
        cy={RING_CENTER}
        r={RING_RADIUS}
        fill={theme.colors.get(innerColor)}
        fillOpacity={0.49}
      />
      <circle
        cx={RING_CENTER}
        cy={RING_CENTER}
        r={RING_RADIUS}
        fill="none"
        stroke={theme.colors.get(trackColor)}
        strokeWidth={RING_STROKE_WIDTH}
      />
      <circle
        cx={RING_CENTER}
        cy={RING_CENTER}
        r={RING_RADIUS}
        fill="none"
        stroke={theme.colors.get(progressColor)}
        strokeWidth={RING_STROKE_WIDTH}
        strokeLinecap="round"
        strokeDasharray={RING_CIRCUMFERENCE}
        strokeDashoffset={RING_CIRCUMFERENCE * (1 - progress)}
        transform={`rotate(-90 ${RING_CENTER} ${RING_CENTER})`}
      />
    </StatRingSvg>
  );
};

StatProgressRing.propTypes = {
  stat: PropTypes.number.isRequired,
  size: PropTypes.number.isRequired,
  innerColor: PropTypes.string.isRequired,
  trackColor: PropTypes.string.isRequired,
  progressColor: PropTypes.string.isRequired,
};

export const StatBadge = ({
  stat,
  icon,
  color = 'secondary.plum',
  backgroundColor = 'background.lightest',
  size = 72,
}) => {
  const hasProgressRing = typeof stat === 'number';
  const ringTrackColor = 'hero.lilac';
  const ringInnerColor = 'hero.lilac';

  return (
    <StyledStatBadge
      $color={color}
      $backgroundColor={backgroundColor}
      $size={size}
      $hasProgressRing={hasProgressRing}
    >
      {hasProgressRing ? (
        <>
          <StatProgressRing
            stat={stat}
            size={size}
            innerColor={ringInnerColor}
            trackColor={ringTrackColor}
            progressColor={color}
          />
          <StatText $color={color}>{stat}%</StatText>
        </>
      ) : (
        icon?.src && (
          <StatIcon
            role="img"
            aria-label={icon.alt}
            $src={icon.src}
            $color={color}
            $iconSize={Math.round(size * 0.5)}
          />
        )
      )}
    </StyledStatBadge>
  );
};

StatBadge.displayName = 'StatBadge';

StatBadge.propTypes = {
  stat: PropTypes.number,
  icon: PropTypes.shape({
    src: PropTypes.string.isRequired,
    alt: PropTypes.string,
  }),
  color: PropTypes.string,
  backgroundColor: PropTypes.string,
  size: PropTypes.number,
};
